import type { FC } from 'react';
import React from 'react';
import { useTheme } from '@mui/material/styles';
import { IconButton, Skeleton, Stack, styled, Typography } from '@mui/material';
import { Box, Iconify, PdfFileIcon, Tooltip } from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';

interface NavigationBarImagePreviewProps {
  imageUrl: string;
  fileName: string;
  isPdf: boolean;
  isImage: boolean;
  selected: boolean;
  onImageSelect: (imageUrl: string) => void;
  isLoading: boolean;
  onDelete?: () => void;
}

export const NavigationBarImagePreview: FC<NavigationBarImagePreviewProps> = ({
  imageUrl,
  fileName,
  selected,
  isPdf,
  isImage,
  onImageSelect,
  isLoading,
  onDelete,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const preview = isLoading ? (
    <Skeleton variant="rectangular" width={60} height={60} />
  ) : isPdf ? (
    <SmallPdfPreview selected={selected} />
  ) : isImage ? (
    <img alt="" src={imageUrl} style={{ width: '120%' }} />
  ) : (
    <Typography
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      fontSize={10}
      textAlign={'center'}
    >
      {t('common.file-type-not-supported-no-file-type')}
    </Typography>
  );

  const showDelete = !isLoading && onDelete !== undefined;

  return (
    <Tooltip title={isLoading ? '' : fileName} placement="top">
      <SmallImagePreview
        selected={selected}
        pinkColor={theme.palette.secondary.dark}
        onClick={() => onImageSelect(imageUrl)}
      >
        {preview}
        {/* Revealed on hover (ENG-2295) so the preview area below needs no
            permanent button strip. Delete is the only action: replacing a
            receipt is the same as deleting it and adding the new one, so a
            second button only made both of them too small to hit. */}
        {showDelete && (
          <ThumbnailActionButton
            className="thumbnail-actions"
            aria-label={t('actions.delete')}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <Iconify icon="eva:trash-2-outline" fontSize={18} />
          </ThumbnailActionButton>
        )}
      </SmallImagePreview>
    </Tooltip>
  );
};

const SmallPdfPreview: FC<{ selected: boolean }> = ({ selected }) => {
  const theme = useTheme();

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        pointerEvents: 'none',
        border: selected ? '0px solid' : '2px dashed',
        borderColor: selected ? 'none' : theme.palette.grey['300'],
        borderRadius: 2,
        objectFit: 'cover',
        width: '100%',
        height: '100%',
      }}
    >
      <img
        alt="pdf file icon"
        src={PdfFileIcon}
        style={{
          height: 38,
        }}
      />
    </Stack>
  );
};

interface SmallImagePreviewProps {
  selected: boolean;
  pinkColor: string;
}

const SmallImagePreview = styled(Box)<SmallImagePreviewProps>(
  ({ selected, pinkColor }) =>
    ({ theme }) =>
      theme.unstable_sx({
        position: 'relative',
        flexShrink: 0,
        borderRadius: 2,
        margin: 0.5,
        // Not on the wrapper: a parent filter desaturates its children, which
        // would grey out the red delete button too.
        '& > :not(.thumbnail-actions)': {
          filter: selected ? 'none' : 'grayscale(1)',
        },
        height: 60,
        width: 60,
        bgcolor: 'white',
        border: selected ? '3px solid' : 0,
        borderColor: selected ? pinkColor : 'none',
        overflow: 'hidden',
        '&:hover .thumbnail-actions': {
          display: 'flex',
        },
      })
);

// Bottom-right corner of the thumbnail, so it covers as little of the receipt
// as possible while still being a full-height corner target.
const ThumbnailActionButton = styled(IconButton)(({ theme }) =>
  theme.unstable_sx({
    display: 'none',
    position: 'absolute',
    bottom: 0,
    right: 0,
    padding: 0.25,
    borderRadius: 0,
    color: 'error.main',
    bgcolor: 'rgb(255,255,255,0.85)',
    '&:hover': { bgcolor: 'rgb(255,255,255,0.95)' },
  })
);
